import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { makeStyles } from '@mui/styles';
import { Box } from '@mui/material';
import Skeleton from '@mui/material/Skeleton';

import colors from '../../../../../../constants/colors';
import UserRoles from '../../../../../../constants/userRoles';

const useStyles = makeStyles(() => ({
  container: {
    margin: '0 14px',
  },
  title: {
    fontWeight: '600',
    display: 'flex',
    alignItems: 'center',
    color: colors.blue,
    marginTop: '8px',
    marginBottom: '6px',
    fontSize: '1.6rem',
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    textAlign: 'center',
    marginBottom: '6px',
    flexWrap: 'wrap',
    gap: '4px',
  },
  box: {
    '@media (min-height:768px)': { fontSize: '1.4rem' },
    fontSize: '1.4rem',
    fontWeight: '600',
    lineHeight: '120%',
    height: '33px',
    paddingLeft: '3px',
    paddingRight: '3px',
    minWidth: '70px',
    alignItems: 'center',
    background: 'linear-gradient(135deg, #e8f7fc 0%, #cdf0f8 100%)',
    border: '1px solid #a0d8ef',
    borderRadius: '4px',
    color: '#2a3a42',
    cursor: 'pointer',
    display: 'flex',
    justifyContent: 'center',
    transition: 'all 0.2s ease',
    '&:hover': {
      background: 'linear-gradient(135deg, #c8eef8 0%, #a0e0f5 100%)',
      color: '#1a5fa8',
    },
  },
  boxActive: {
    '@media (min-height:768px)': { fontSize: '1.4rem' },
    fontSize: '1.4rem',
    fontWeight: '600',
    lineHeight: '120%',
    minWidth: '70px',
    height: '33px',
    paddingLeft: '3px',
    paddingRight: '3px',
    alignItems: 'center',
    background: 'linear-gradient(135deg, #00d5fd 0%, #0495ff 50%, #0072ee 100%)',
    border: '1px solid transparent',
    borderRadius: '4px',
    boxShadow: '0 2px 8px rgba(0, 180, 220, 0.3)',
    color: colors.white,
    cursor: 'pointer',
    display: 'flex',
    justifyContent: 'center',
    transition: 'all 0.2s ease',
  },
  skeleton: {
    padding: '0 5px 0 3px'
  }
}));

const $FIXEDWIDTH = 70;
const $FIXEDHEIGHT = 33;

const Filter = ({ tabs, onFilterClick, clearFilters }) => {
  const user = useSelector((state) => state.User);
  const classes = useStyles('');
  const [rulesType, setRulesType] = useState([]);
  const [rulesTypeStyles, setRulesTypeStyles] = useState({});
  const [alertsType, setAlertsType] = useState([]);
  const [alertsTypeStyles, setAlertsTypeStyles] = useState({});

  const getIsDisabledRuleType = (ruleTypeName) => {
    let result = false;
    if (user.Role === UserRoles.WFM && user.Role !== UserRoles.Admin && user.Role !== UserRoles.Owner) {
      if (!(user.Claims.RulesConfigurationEnterpriseSearch) && ruleTypeName === 'Enterprise') {
        result = true;
      }

      if (!(user.Claims.RulesConfigurationCustomSearch) && ruleTypeName === 'Custom') {
        result = true;
      }
    }

    return result;
  }

  const handleRuleType = (tabFilter) => {
    const $ID = tabFilter.name === rulesTypeStyles.name ? null : tabFilter.id;
    const ruleType = tabFilter.name === rulesTypeStyles.name ? {} : tabFilter;
    if (!getIsDisabledRuleType(tabFilter.name)) {
      onFilterClick({ type: 'rule', id: $ID });
      setRulesTypeStyles(ruleType);
    }
  };
  const handleAlertType = (tabFilter) => {
    const $ID = tabFilter.name === alertsTypeStyles.name ? null : tabFilter.id;
  const alertType = tabFilter.name === alertsTypeStyles.name ? {} : tabFilter;
    onFilterClick({ type: 'alert', id: $ID });
    setAlertsTypeStyles(alertType);
  };
  const LoadingRuleFilters = () => (
    <>
      <Skeleton className={classes.skeleton} width={$FIXEDWIDTH} height={$FIXEDHEIGHT} />
      <Skeleton className={classes.skeleton} width={$FIXEDWIDTH} height={$FIXEDHEIGHT} />
    </>
    )
  const LoadingAlarmFilters = () => (
      <>
        <Skeleton className={classes.skeleton} width={$FIXEDWIDTH} height={$FIXEDHEIGHT} />
        <Skeleton className={classes.skeleton} width={$FIXEDWIDTH} height={$FIXEDHEIGHT} />
        <Skeleton className={classes.skeleton} width={$FIXEDWIDTH} height={$FIXEDHEIGHT} />
      </>
    )

  useEffect(() => {
    if (tabs) {
      setRulesType(tabs.ruleTypes);

      // If actual rule type set is disallowed by role permission
      if (getIsDisabledRuleType(rulesTypeStyles.name)) {

        // Validate all rule types to find if there is any allowed
        tabs.ruleTypes.map((ruleType) => {

          // If allowed, apply style changes (enable Client Parent Access search fields if Custom, disable them if Enterprise)
          if (!getIsDisabledRuleType(ruleType.name)) {
            onFilterClick({ type: 'rule', id: ruleType.id });
            return setRulesTypeStyles(ruleType);
          }
          return null;
        });
      }

      setAlertsType(tabs.alertTypes);
    }
  }, [tabs]);
  useEffect(() => {
    if (clearFilters) {
      setRulesTypeStyles({});
      setAlertsTypeStyles({});
    }
  }, [clearFilters])

  return (
    <Box className={classes.container}>
      <Box className={classes.title}>
        Filters
      </Box>
      <Box className={classes.row}>
        {rulesType.length ? rulesType.map((tab) => (
          <Box
            className={rulesTypeStyles && rulesTypeStyles.name === tab.name
              ? classes.boxActive : classes.box}
            key={tab.id}
            onClick={() => handleRuleType(tab)}
          >
            {tab.name}
          </Box>
        ))
      : <LoadingRuleFilters />
      }
      </Box>
      <Box className={classes.row}>
        {alertsType.length ? alertsType.map((tab) => (
          <Box
            className={alertsTypeStyles && alertsTypeStyles.name === tab.name
              ? classes.boxActive : classes.box}
            key={tab.id}
            onClick={() => handleAlertType(tab)}
          >
            {tab.name}
          </Box>
        ))
      : <LoadingAlarmFilters />
      }
      </Box>
    </Box>
  );
};

Filter.propTypes = {
  onFilterClick: PropTypes.func.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  tabs: PropTypes.object,
  clearFilters: PropTypes.bool
};

Filter.defaultProps = {
  tabs: undefined,
  clearFilters: false
}
export default Filter;
